import React, { useCallback, useRef, useState } from 'react';
import { Camera, X, Loader2, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useImageUrl } from '@/hooks/useImage';
import { formatFileSize, getMaxFileSize, validateFile, STORAGE_CONFIG, getMimeTypeFromExtension } from '@/config/storage';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { Trans } from '@lingui/macro';
import { toast } from 'sonner';
import { images_GenerateUploadURLRequest } from '@/types/image';

export interface AvatarUploadProps {
  value?: string;
  onChange?: (imageId: string | undefined) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  entityType?: string;
  entityId?: string;
  className?: string;
  disabled?: boolean;
  fallbackIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  currentImageUrl?: string;
  currentImageId?: string;
}

const sizeClasses = {
  sm: 'h-16 w-16',
  md: 'h-24 w-24',
  lg: 'h-32 w-32',
  xl: 'h-40 w-40',
};

export function AvatarUpload({
  value,
  onChange,
  onUploadingChange,
  entityType,
  entityId,
  className,
  disabled = false,
  fallbackIcon = <User className="h-1/2 w-1/2" />,
  size = 'lg',
  currentImageUrl,
  currentImageId,
}: AvatarUploadProps) {
  const { i18n } = useLingui();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  
  // Resolve image URL from imageId if provided
  // When value is explicitly set (including to empty), use it. Only fallback to currentImageId when value is undefined
  // This ensures that when a user removes an image (value = ''), we respect that choice
  // instead of showing the original image
  const effectiveImageId = value !== undefined ? value : currentImageId;
  const resolvedImageUrl = useImageUrl(effectiveImageId);
  
  // Use resolved URL, fallback to provided URL, or null
  const displayUrl = preview || resolvedImageUrl || currentImageUrl || null;

  const { uploadFile, uploadProgress, isUploading } = useImageUpload({
    purpose: images_GenerateUploadURLRequest.purpose.AVATAR,
    entityType,
    entityId,
    onSuccess: (imageId) => {
      onChange?.(imageId);
    },
    onError: () => {
      setPreview(null);
    },
  });

  // Define processing state early for use in all callbacks
  const isProcessing = isUploading;

  // Notify parent component of upload state changes
  React.useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Normalize MIME type for reliable checking
    let normalizedMimeType = file.type.toLowerCase().trim();
    
    // If MIME type is empty or unreliable, try to determine from file extension
    if (!normalizedMimeType || normalizedMimeType === 'application/octet-stream') {
      const mimeTypeFromExtension = getMimeTypeFromExtension(file.name);
      if (mimeTypeFromExtension) {
        normalizedMimeType = mimeTypeFromExtension;
      }
    }
    
    // Strict validation using storage config
    const allowedMimeTypes: readonly string[] =
      STORAGE_CONFIG.allowedMimeTypes.avatar;
    if (!allowedMimeTypes.includes(normalizedMimeType)) {
      toast.error(
        i18n._(
          msg({
            id: 'profile.avatar.invalidFileType',
            message: 'Invalid file type. Please upload an image.',
          })
        )
      );
      return;
    }
    
    // Additional validation using the comprehensive validate function
    const validation = validateFile(file, 'avatar');
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadFile(file, i18n._(msg`profile.avatar.imageDescription`));
  }, [uploadFile, i18n]);

  const handleClick = useCallback(() => {
    if (!isProcessing && !disabled) {
      fileInputRef.current?.click();
    }
  }, [isProcessing, disabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isProcessing && !disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [isProcessing, disabled]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Safety check to match other handlers
    if (isProcessing || disabled) {
      return;
    }
    
    // Only update form state - no API calls
    setPreview(null);
    onChange?.(undefined);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange, isProcessing, disabled]);

  const hasImage = !!displayUrl;
  const maxSize = getMaxFileSize(images_GenerateUploadURLRequest.purpose.AVATAR);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || isProcessing}
        className="sr-only"
      />

      <div
        className={cn(
          'relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full',
          (isProcessing || disabled) && 'cursor-not-allowed opacity-50'
        )}
        role="button"
        tabIndex={isProcessing || disabled ? -1 : 0}
        aria-label={i18n._(hasImage ? msg`profile.avatar.change` : msg`profile.avatar.upload`)}
        aria-disabled={isProcessing || disabled}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <Avatar className={cn(sizeClasses[size], 'border-2 border-muted')}>
          {displayUrl ? (
            <AvatarImage 
              src={displayUrl} 
              alt={i18n._(msg`profile.avatar.alt`)} 
            />
          ) : null}
          <AvatarFallback>
            {isProcessing ? (
              <Loader2 className="h-1/2 w-1/2 animate-spin" />
            ) : (
              fallbackIcon
            )}
          </AvatarFallback>
        </Avatar>

        {/* Upload overlay */}
        {!isProcessing && !disabled && isHovering && !hasImage && (
          <div className={cn(
            'absolute inset-0 rounded-full bg-black/50 flex items-center justify-center',
            'transition-opacity',
            sizeClasses[size]
          )}>
            <Camera className="h-1/3 w-1/3 text-white" />
          </div>
        )}

        {/* Change/Remove buttons when hovering over existing image */}
        {/* Show buttons when hovering and there's an image - disable based on processing or disabled state */}
        {isHovering && hasImage && (
          <div className={cn(
            'absolute inset-0 rounded-full bg-black/50 flex items-center justify-center',
            'transition-opacity',
            sizeClasses[size]
          )}>
            <div className="flex gap-2">
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="h-8 w-8 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                disabled={isProcessing || disabled}
                title={i18n._(msg`profile.avatar.changeButton`)}
                aria-label={i18n._(msg`profile.avatar.changeButton`)}
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="h-8 w-8 rounded-full"
                onClick={handleRemove}
                disabled={isProcessing || disabled}
                title={i18n._(msg`profile.avatar.removeButton`)}
                aria-label={i18n._(msg`profile.avatar.removeButton`)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}

        {/* Upload progress indicator */}
        {isUploading && (
          <div className="absolute -bottom-6 left-0 right-0 text-center">
            <p className="text-xs text-muted-foreground">
              {uploadProgress.progress}%
            </p>
          </div>
        )}
      </div>

      {/* Help text */}
      {!hasImage && !isProcessing && (
        <p className="text-xs text-muted-foreground text-center mt-3">
          <Trans id="profile.avatar.clickToUpload">Click to upload</Trans>
          <span className="text-xs"> • </span>
          <span className="text-xs">{i18n._(
              msg({
                id: 'profile.avatar.maxSize',
                message: 'Maximum file size: {maxSize}',
              }),
              { maxSize: formatFileSize(maxSize) }
            )}</span>
        </p>
      )}
    </div>
  );
}